
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/base/Header';
import Footer from '../../components/base/Footer';
import { useCart } from '../../hooks/useCart';

const CartPage = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, getSubtotal } = useCart();
  const [isLoading, setIsLoading] = useState(false);

  const handleQuantityChange = (id: string, size: string | undefined, newQuantity: number) => {
    if (newQuantity >= 0 && newQuantity <= 99) {
      updateQuantity(id, size, newQuantity);
    }
  };

  const handleContinueToPayment = () => {
    if (items.length === 0) return;
    setIsLoading(true);
    setTimeout(() => {
      navigate('/checkout');
      setIsLoading(false);
    }, 500);
  };

  const formatPrice = (price: string) => {
    return price;
  };

  const formatTotal = (total: number) => {
    return `R$ ${total.toFixed(2).replace('.', ',')}`;
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F8F8F8' }}>
        <Header />
        
        <div className="pt-24 pb-16">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <div 
                className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#C0C0C0' }}
              >
                <i className="ri-shopping-bag-line text-4xl text-white"></i>
              </div>
              
              <h1 
                className="text-3xl font-bold mb-4"
                style={{ 
                  fontFamily: 'Cinzel Decorative, serif',
                  color: '#0B0B0B'
                }}
              >
                Seu carrinho está vazio
              </h1>
              
              <p 
                className="text-lg mb-8"
                style={{ 
                  color: '#C0C0C0',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                Explore nossa coleção e adicione produtos ao seu carrinho
              </p>
              
              <button
                onClick={() => navigate('/masculino')}
                className="px-8 py-3 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-green-700"
                style={{ 
                  backgroundColor: '#0F3F2F',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                Explorar Produtos
              </button>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F8F8' }}>
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 
                className="text-3xl font-bold mb-2"
                style={{ 
                  fontFamily: 'Cinzel Decorative, serif',
                  color: '#0B0B0B'
                }}
              >
                Carrinho de Compras
              </h1>
              <p 
                className="text-sm"
                style={{ 
                  color: '#C0C0C0',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                {items.length} {items.length === 1 ? 'item' : 'itens'} no carrinho
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div 
                      key={`${item.id}-${item.size || 'default'}-${index}`}
                      className="bg-white rounded-lg p-6 shadow-sm border"
                      style={{ borderColor: '#C0C0C0' }}
                    >
                      <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-6">
                        {/* Product Image */}
                        <div className="w-full md:w-24 h-48 md:h-24 flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <p 
                            className="text-xs font-medium uppercase tracking-wide mb-1"
                            style={{ 
                              color: '#C0C0C0',
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            {item.brand}
                          </p>
                          <h3 
                            className="text-sm font-medium mb-2"
                            style={{ 
                              color: '#0B0B0B',
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            {item.name}
                          </h3>
                          {item.size && (
                            <p 
                              className="text-xs mb-2"
                              style={{ 
                                color: '#C0C0C0',
                                fontFamily: 'Montserrat, sans-serif'
                              }}
                            >
                              Tamanho: {item.size}
                            </p>
                          )}
                          <p 
                            className="text-lg font-bold"
                            style={{ 
                              color: '#0F3F2F',
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            {formatPrice(item.price)}
                          </p>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center border rounded-lg" style={{ borderColor: '#C0C0C0' }}>
                            <button
                              onClick={() => handleQuantityChange(item.id, item.size, item.quantity - 1)}
                              className="p-2 hover:bg-gray-50 transition-colors duration-200"
                              disabled={item.quantity <= 1}
                            >
                              <i className="ri-subtract-line text-sm" style={{ color: '#0B0B0B' }}></i>
                            </button>
                            <span 
                              className="px-4 py-2 text-sm font-medium min-w-[3rem] text-center"
                              style={{ 
                                color: '#0B0B0B',
                                fontFamily: 'Montserrat, sans-serif'
                              }}
                            >
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => handleQuantityChange(item.id, item.size, item.quantity + 1)}
                              className="p-2 hover:bg-gray-50 transition-colors duration-200"
                              disabled={item.quantity >= 99}
                            >
                              <i className="ri-add-line text-sm" style={{ color: '#0B0B0B' }}></i>
                            </button>
                          </div>

                          <button
                            onClick={() => removeItem(item.id, item.size)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div 
                  className="bg-white rounded-lg p-6 shadow-sm border sticky top-24"
                  style={{ borderColor: '#C0C0C0' }}
                >
                  <h2 
                    className="text-xl font-bold mb-6"
                    style={{ 
                      fontFamily: 'Playfair Display, serif',
                      color: '#0B0B0B'
                    }}
                  >
                    Resumo do Pedido
                  </h2>

                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between">
                      <span 
                        className="text-sm"
                        style={{ 
                          color: '#C0C0C0',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        Subtotal ({items.reduce((total, item) => total + item.quantity, 0)} itens)
                      </span>
                      <span 
                        className="text-sm font-medium"
                        style={{ 
                          color: '#0B0B0B',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        {formatTotal(getSubtotal())}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span 
                        className="text-sm"
                        style={{ 
                          color: '#C0C0C0',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        Frete
                      </span>
                      <span 
                        className="text-sm"
                        style={{ 
                          color: '#C0C0C0',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        Calculado no checkout
                      </span>
                    </div>

                    <div 
                      className="border-t pt-4"
                      style={{ borderColor: '#C0C0C0' }}
                    >
                      <div className="flex justify-between">
                        <span 
                          className="text-lg font-bold"
                          style={{ 
                            color: '#0B0B0B',
                            fontFamily: 'Montserrat, sans-serif'
                          }}
                        >
                          Total
                        </span>
                        <span 
                          className="text-lg font-bold"
                          style={{ 
                            color: '#0F3F2F',
                            fontFamily: 'Montserrat, sans-serif'
                          }}
                        >
                          {formatTotal(getSubtotal())}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleContinueToPayment}
                    disabled={isLoading}
                    className="w-full py-3 px-6 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-green-700 disabled:opacity-50"
                    style={{ 
                      backgroundColor: '#0F3F2F',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    {isLoading ? 'Carregando...' : 'Finalizar Compra'}
                  </button>

                  <button
                    onClick={() => navigate('/masculino')}
                    className="w-full mt-3 py-3 px-6 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-300"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Continuar Comprando
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
